// @ts-nocheck
import { getCurrentUser } from "../auth/session";
import {
  loadTributesForView,
  resetTributesView,
} from "../features/memorial-tributes";
import { getMyHeadstones } from "../features/posts-access";
import { trackMetaEvent } from "../utils/meta-events";
import {
  bindPetPhotoFallback,
  mountPetPhoto,
  renderPetPhotoElement,
} from "../utils/pet-photo-placeholder";
import { flowerIconSvg } from "../utils/tribute-icons";
import { tombstoneStyleClass } from "../utils/headstone-styles";
import { mapState } from "./state";
import { resetGardenZoom } from "./camera";
import { applyCandleGlow } from "./candle-glow";
import { isMobileViewport } from "../utils/viewport";
import {
  bindFamilySection,
  loadHeadstoneMembers,
  renderFamilySection,
} from "../features/family-share";
import { t } from "../i18n";
import { refreshAds } from "../features/ads";
import {
  isApplyingGardenLocation,
  setGardenFocusLocation,
  setMemorialViewLocation,
  syncGardenDocumentTitle,
} from "../features/garden-location";
import { showView } from "../features/view-router";
import { coordsFromHoleId } from "../utils/hole-id";

function upsertTombFlower(tomb: HTMLElement, flowerCount = 0) {
  let el = tomb.querySelector(".tomb-flower-gift") as HTMLElement | null;
  if (!flowerCount) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement("div");
    el.className = "tomb-flower-gift";
    el.setAttribute("aria-hidden", "true");
    tomb.appendChild(el);
  }
  el.innerHTML = flowerIconSvg(14);
}

function applyTombStyle(tomb: HTMLElement, styleKey?: string | null) {
  tomb.className = tombstoneStyleClass(styleKey);
}

export function canManageViewedHeadstone(viewData): boolean {
  const user = getCurrentUser();
  if (!user || !viewData) return false;
  if (viewData.ownerId && user.id === viewData.ownerId) return true;
  return getMyHeadstones().some((h) => h.id === viewData.id);
}

function insertTombPhoto(tomb: HTMLElement, node: HTMLElement) {
  const cross = tomb.querySelector(".tomb-cross");
  const tombInfo = tomb.querySelector(".tomb-info");
  if (cross && tombInfo) tomb.insertBefore(node, tombInfo);
  else if (cross) cross.after(node);
  else tomb.prepend(node);
}

function upsertTombPhoto(
  tomb: HTMLElement,
  info: { photo?: string | null; name?: string; id?: string; holeId?: string },
) {
  const existing = tomb.querySelector(".tomb-photo");
  const nextSrc = info.photo ?? "";
  if (existing && tomb.dataset.photoSrc === nextSrc) return;
  tomb.dataset.photoSrc = nextSrc;
  if (existing) existing.remove();

  const seed = info.id ?? info.holeId ?? info.name ?? "tomb";

  if (info.photo) {
    const ph = document.createElement("img");
    ph.className = "tomb-photo";
    ph.loading = "lazy";
    ph.crossOrigin = "anonymous";
    ph.alt = info.name ?? "";
    ph.src = info.photo;
    bindPetPhotoFallback(ph, {
      variant: "tomb",
      seed,
      name: info.name,
      className: "tomb-photo pet-photo pet-photo--tomb",
    });
    insertTombPhoto(tomb, ph);
    return;
  }

  const placeholder = renderPetPhotoElement({
    variant: "tomb",
    photoUrl: null,
    seed,
    name: info.name,
    className: "tomb-photo pet-photo pet-photo--tomb pet-photo--placeholder",
  });
  insertTombPhoto(tomb, placeholder);
}

export function renderGravePlaceholder(el: HTMLElement, opts: { holeId: string }): void {
  if (el.classList.contains("bought") && !el.classList.contains("hole-spot--loading")) return;

  el.classList.add("bought", "hole-spot--loading");
  el.innerHTML = "";

  const tomb = document.createElement("div");
  tomb.className = "tombstone tombstone--loading";

  const cross = document.createElement("div");
  cross.className = "tomb-cross";
  cross.innerHTML = "&#x2764;";
  tomb.appendChild(cross);

  const photo = renderPetPhotoElement({
    variant: "tomb",
    photoUrl: null,
    seed: opts.holeId,
    className: "tomb-photo pet-photo pet-photo--tomb pet-photo--placeholder",
  });
  insertTombPhoto(tomb, photo);

  const info = document.createElement("div");
  info.className = "tomb-info";
  info.innerHTML = `
    <span class="tomb-skeleton-line tomb-skeleton-line--name"></span>
    <span class="tomb-skeleton-line tomb-skeleton-line--epitaph"></span>
  `;
  tomb.appendChild(info);
  el.appendChild(tomb);
}

export function restoreGrave(el, info) {
  const isLoadingPlaceholder = el.classList.contains("hole-spot--loading");

  if (!el.classList.contains("bought") || isLoadingPlaceholder) {
    el.classList.remove("hole-spot--loading");
    el.classList.add("bought");
    el.innerHTML = "";
    const tomb = document.createElement("div");
    tomb.className = tombstoneStyleClass(info.styleKey);
    const cross = document.createElement("div");
    cross.className = "tomb-cross";
    cross.innerHTML = "&#x2764;";
    tomb.appendChild(cross);

    const id = document.createElement("div");
    id.className = "tomb-info";
    const n = document.createElement("div");
    n.className = "t-name";
    n.innerText = info.name;
    const d = document.createElement("div");
    d.className = "t-date";
    d.innerText = info.dateLabel || "";
    const meta = document.createElement("div");
    meta.className = "t-meta";
    meta.innerText = info.metaLabel || "";
    const ep = document.createElement("div");
    ep.className = "t-epitaph";
    ep.innerText = info.epitaph;
    id.appendChild(n);
    if (info.dateLabel) id.appendChild(d);
    if (info.metaLabel) id.appendChild(meta);
    id.appendChild(ep);
    tomb.appendChild(id);
    upsertTombPhoto(tomb, info);
    upsertTombFlower(tomb, info.flowerCount);
    el.appendChild(tomb);
  } else {
    const tDate = el.querySelector(".t-date");
    const tMeta = el.querySelector(".t-meta");
    const tName = el.querySelector(".t-name");
    const tEp = el.querySelector(".t-epitaph");
    if (tName) tName.textContent = info.name;
    if (tDate) {
      tDate.textContent = info.dateLabel || "";
      tDate.style.display = info.dateLabel ? "" : "none";
    }
    if (tMeta) {
      tMeta.textContent = info.metaLabel || "";
      tMeta.style.display = info.metaLabel ? "" : "none";
    } else if (info.metaLabel) {
      const meta = document.createElement("div");
      meta.className = "t-meta";
      meta.textContent = info.metaLabel;
      const tombInfo = el.querySelector(".tomb-info");
      const ep = el.querySelector(".t-epitaph");
      if (tombInfo && ep) tombInfo.insertBefore(meta, ep);
    }
    if (tEp) tEp.textContent = info.epitaph;
    const tomb = el.querySelector(".tombstone") as HTMLElement | null;
    if (tomb) {
      applyTombStyle(tomb, info.styleKey);
      upsertTombPhoto(tomb, info);
      upsertTombFlower(tomb, info.flowerCount);
    }
  }
  applyCandleGlow(el, info.candleCount);
  el.__graveData = info;
}

export function openViewModal(el) {
  const holeId = el.dataset.id;
  const data =
    el.__graveData ?? (holeId ? mapState.gravesData[holeId] : null);
  if (!data) return;
  el.__graveData = data;

  const viewData = el.__graveData;
  const viewModal = document.getElementById("view-modal");
  const viewDate = document.getElementById("view-date");
  const viewMeta = document.getElementById("view-meta");
  const viewEditBtn = document.getElementById("view-edit-btn");

  document.getElementById("view-name").innerText = viewData.name;

  if (viewDate) {
    const sheetDate = viewData.fullDateLabel || viewData.dateLabel || "";
    viewDate.innerText = sheetDate;
    viewDate.style.display = sheetDate ? "block" : "none";
  }
  if (viewMeta) {
    viewMeta.innerText = viewData.metaLabel || "";
    viewMeta.style.display = viewData.metaLabel ? "block" : "none";
  }

  const epitaphText = viewData.epitaph
    ? `"${viewData.epitaph}"`
    : "";
  document.getElementById("view-epitaph").innerText = epitaphText;

  const photoContainer = document.getElementById("view-photo-container");
  if (photoContainer) {
    mountPetPhoto(photoContainer, {
      variant: "view-hero",
      photoUrl: viewData.photo,
      seed: viewData.id ?? viewData.holeId,
      name: viewData.name,
    });
  }
  renderViewGallery(viewData.gallery);

  const user = getCurrentUser();
  const isOwner = !!(user && viewData.ownerId && user.id === viewData.ownerId);
  const isManager = canManageViewedHeadstone(viewData);
  if (viewEditBtn) {
    viewEditBtn.style.display = isManager ? "block" : "none";
  }
  (window as any).__editingHeadstone = isManager ? viewData : null;
  (window as any).__viewingHeadstone = viewData;

  if (viewData.id) {
    loadTributesForView(viewData.id, isManager).catch(() => null);
    refreshFamilySection(viewData.id, isOwner, isManager).catch(() => null);
  } else {
    const familyHost = document.getElementById("view-family-host");
    if (familyHost) familyHost.innerHTML = "";
  }

  trackMetaEvent("ViewContent", {
    content_ids: viewData.id ? [viewData.id] : undefined,
    content_type: "product",
    content_name: viewData.name,
  });

  if (viewModal) viewModal.style.display = "flex";
  showView("garden");
  if (viewData.id && !isApplyingGardenLocation()) {
    setMemorialViewLocation(viewData.id);
  }
  syncGardenDocumentTitle(viewData.name);
  syncMemorialAdSlot(viewData);
}

function syncMemorialAdSlot(viewData): void {
  const slot = document.getElementById("memorial-ad-slot");
  if (!slot) return;
  const hasName = !!(viewData?.name && String(viewData.name).trim());
  const hasPhoto = !!(viewData?.photo && String(viewData.photo).trim());
  const hasEpitaph = !!(viewData?.epitaph && String(viewData.epitaph).trim());
  const ready = hasName && (hasPhoto || hasEpitaph);
  slot.setAttribute("data-ads-ready", ready ? "1" : "0");
  if (!ready) {
    slot.innerHTML = "";
    slot.hidden = true;
  }
  refreshAds();
}

function renderViewGallery(gallery) {
  const host = document.getElementById("view-gallery");
  if (!host) return;
  const items = Array.isArray(gallery) ? gallery : [];
  if (!items.length) {
    host.hidden = true;
    host.innerHTML = "";
    return;
  }
  host.hidden = false;
  host.innerHTML = items
    .map((item) => {
      if (item.kind === "video") {
        return `<video class="view-gallery__item" src="${item.url}" controls playsinline></video>`;
      }
      return `<img class="view-gallery__item" src="${item.url}" alt="">`;
    })
    .join("");
}

async function refreshFamilySection(headstoneId, isOwner, isManager) {
  const host = document.getElementById("view-family-host");
  if (!host) return;
  if (!isManager) {
    host.innerHTML = "";
    return;
  }
  try {
    const members = await loadHeadstoneMembers(headstoneId);
    host.innerHTML = renderFamilySection({ members, isOwner });
    bindFamilySection(headstoneId, isOwner, () => {
      refreshFamilySection(headstoneId, isOwner, isManager).catch(() => null);
    });
  } catch {
    host.innerHTML = `<p class="view-tributes-hint">${t("pethernity.familyLoadError")}</p>`;
  }
}

export function closeViewModal(options: { persistGardenLocation?: boolean } = {}) {
  const persist = options.persistGardenLocation !== false;
  const viewed = (window as any).__viewingHeadstone;
  const holeId = viewed?.holeId;

  if (isMobileViewport()) resetGardenZoom();
  const viewModal = document.getElementById("view-modal");
  if (viewModal) viewModal.style.display = "none";
  (window as any).__viewingHeadstone = null;
  resetTributesView();
  const slot = document.getElementById("memorial-ad-slot");
  if (slot) {
    slot.setAttribute("data-ads-ready", "0");
    slot.innerHTML = "";
    slot.hidden = true;
  }
  refreshAds();

  if (persist) {
    showView("garden");
    if (holeId) {
      const [x, y] = coordsFromHoleId(holeId);
      setGardenFocusLocation(x, y);
    }
    syncGardenDocumentTitle(null);
  }
}
